import {
  composeSelector,
  composeSelectors
} from "seneca-common/utils/selectors/compose-selectors";

import { PATH_TO_COURSES_STATE } from "../../const";
import { Contents } from "../../models/state/contents/Contents";
import { Courses } from "../../models/state/courses/Courses";
import CoursesStateSlice from "../../models/state/CoursesStateSlice";
import { Modules } from "../../models/state/modules/Modules";
import { Sections } from "../../models/state/sections/Sections";
import makeCourseSelectors from "../makeCourseSelectors";
import * as contentsSelectors from "./contents";
import * as modulesSelectors from "./modules";
import * as sectionsSelectors from "./sections";

type State = any;

export function getCoursesStateSlice(state: any): CoursesStateSlice {
  return state.get(PATH_TO_COURSES_STATE);
}

// courses
function getCoursesSlice(state: CoursesStateSlice): Courses {
  return state.courses;
}

export const getAllCourses = composeSelector<any, CoursesStateSlice, Courses>(
  getCoursesStateSlice,
  getCoursesSlice
);

export const {
  getCourse,
  getAllCourseIds,
  getCourseName,
  getCourseNames,
  getCourseAuthors,
  getCourseReferences,
  getCourseHeaderImageURL,
  getCourseSectionIds,
  getCourseSubscriptionTier,
  getCourseAvailable,
  courseIsPremium,
  courseIsFree,
  getCourseStartingSectionId,
  getCourseTagIds,
  getRelatedCourseIds,
  courseExists,
  getCourseDescription,
  getCourseRegion,
  getCourseExamQuestionsCount,
  doesCourseHaveExamQuestions
} = makeCourseSelectors(getAllCourses);

// sections
function getSectionsSlice(state: CoursesStateSlice): Sections {
  return state.sections;
}

export const getAllSections = composeSelector<any, CoursesStateSlice, Sections>(
  getCoursesStateSlice,
  getSectionsSlice
);

export const {
  getSection,
  getSections,
  listSections,
  getSectionParentId,
  getSectionTitle,
  getSectionsTitles,
  getSectionContentIds,
  getSectionExerciseContentIds,
  getSectionSectionIds,
  getSectionModuleIds,
  isLeafSection,
  getAllSectionContentIds,
  getSectionExamQuestionsCount,
  doesSectionHaveExamQuestions,
  // should be memoized
  getAllSectionContentIdsFromSections, // should be memoized
  getAllSectionExerciseContentIdsFromSections,
  getAllSectionExerciseContentIds
} = composeSelectors<State, Sections>(getAllSections, sectionsSelectors);

// contents
function getContentsSlice(state: CoursesStateSlice): Contents {
  return state.contents;
}

export const getAllContents = composeSelector<any, CoursesStateSlice, Contents>(
  getCoursesStateSlice,
  getContentsSlice
);

export const {
  getContent,
  getContentId,
  getContentModuleIds,
  getContentOptions,
  contentIsSequential,
  contentIsExercise,
  contentModuleIdIsLastInContent,
  getContentModuleIndex,
  getContentModuleIdAtIndex,
  getFirstContentModuleId,
  getFollowingContentModuleId,
  includesContentModuleId
} = composeSelectors<State, Contents>(getAllContents, contentsSelectors);

// modules
function getContentModulesSlice(state: CoursesStateSlice): Modules {
  return state.modules;
}

export const getAllContentModules = composeSelector<
  any,
  CoursesStateSlice,
  Modules
>(getCoursesStateSlice, getContentModulesSlice);

export const {
  getContentModule,
  getContentModuleId,
  getContentModuleParentId,
  getContentModuleType,
  getContentModuleContent,
  getContentModuleDifficulty
} = composeSelectors<State, Modules>(getAllContentModules, modulesSelectors);
